<script lang="ts">
import type { AsTag } from 'reka-ui';

/**
 * Reason emitted with the `'error'` event when an uploaded file fails internal
 * validation against the `accept` extension list or `maxSizeBytes` size cap.
 *
 * - `'bad-format'`: the file's extension is not in the `accept` list.
 * - `'too-large'`: the file's size exceeds `maxSizeBytes`.
 */
export type TFileUploadErrorReason = 'bad-format' | 'too-large';

/**
 * Props for the `TFileUpload` component.
 *
 * `TFileUpload` is a single-file upload control with three visual states
 * (empty / loading / loaded) and built-in validation against an allow-list of
 * extensions and an optional maximum size in bytes. The selected `File` (or
 * `null`) is exposed via `v-model`; validation failures emit `'error'`
 * **instead of** updating the model.
 *
 * All user-facing strings are passed in as props so the consumer owns i18n;
 * the component does not import a translation function.
 */
export interface TFileUploadProps {
  /**
   * Label shown above the upload box (e.g. `"Adjuntar archivo"`).
   * When omitted, the label slot/element is not rendered.
   */
  label?: string;
  /**
   * Call-to-action text rendered inside the box while no file is attached
   * (e.g. `"Subir archivo"`).
   * @defaultValue 'Subir archivo'
   */
  ctaText?: string;
  /**
   * Text rendered inside the box while `loading` is `true`
   * (e.g. `"Cargando archivo"`).
   * @defaultValue 'Cargando archivo'
   */
  loadingText?: string;
  /**
   * Accessible label for the remove button shown in the loaded state.
   * @defaultValue 'Quitar archivo'
   */
  removeLabel?: string;
  /**
   * Hint displayed beneath the box (bottom-left) listing accepted formats.
   * Hidden in the loaded state.
   */
  formatsHint?: string;
  /**
   * Hint displayed beneath the box (bottom-right) describing the size limit.
   * Hidden in the loaded state.
   */
  maxSizeHint?: string;
  /**
   * Accepted file extensions without the leading dot (e.g. `['csv', 'pdf']`).
   * Used to build the native `accept` attribute and to validate the selected
   * file. When omitted, no extension validation runs.
   */
  accept?: string[];
  /**
   * Maximum file size in bytes. Files larger than this emit
   * `'error'` with reason `'too-large'` and the model is not updated.
   * When omitted, no size validation runs.
   */
  maxSizeBytes?: number;
  /**
   * Controlled loading flag. When `true` the box renders the loading visual
   * and the underlying `<input type="file">` is disabled so the user cannot
   * pick another file mid-upload.
   * @defaultValue false
   */
  loading?: boolean;
  /**
   * Optional id forwarded to the underlying `<input type="file">`. When
   * omitted a stable auto-generated id is used.
   */
  id?: string;
  /**
   * Form field name forwarded to the underlying `<input type="file">`.
   * Required for native form submission (the file is sent under this key).
   */
  name?: string;
  /**
   * Marks the input as required for native HTML5 form validation.
   * @defaultValue false
   */
  required?: boolean;
  /**
   * Disables the picker. The native `<input>` is disabled and the box
   * renders in a non-interactive visual state.
   * @defaultValue false
   */
  disabled?: boolean;
  /**
   * `id` of the `<form>` this input belongs to. Use when the component
   * lives outside the target form in the DOM.
   */
  form?: string;
  /**
   * Element the outer wrapper renders as. Useful for semantic nesting
   * (e.g. `'li'` inside a `<ul>`).
   * @defaultValue 'div'
   */
  as?: AsTag;
}

/**
 * Events emitted by `TFileUpload`.
 */
export interface TFileUploadEmits {
  /**
   * Fired when the selected file fails internal validation. The model is
   * **not** updated in this case — consumers can surface the reason as they
   * wish.
   */
  error: [reason: TFileUploadErrorReason];
}

/**
 * Slots exposed by `TFileUpload`. Each slot defaults to rendering its
 * matching text prop, so most consumers will not need them; provide a slot
 * only when richer content (icons, formatting) is required.
 */
export interface TFileUploadSlots {
  /** Overrides the `label` prop. */
  label: () => unknown;
  /** Overrides the `ctaText` prop in the empty state. */
  cta: () => unknown;
  /** Overrides the `loadingText` prop in the loading state. */
  loading: () => unknown;
  /** Overrides the filename rendering in the loaded state. */
  file: (props: { file: File }) => unknown;
}
</script>

<script setup lang="ts">
import { useForwardedAttrs } from '@composables/forwardedAttrs';
import { Primitive } from 'reka-ui';
import { computed, onMounted, ref, useId, watch } from 'vue';
import { TIcon } from '../TIcon';
import { TTypography } from '../TTypography';

defineOptions({
  name: 'TFileUpload',
  inheritAttrs: false,
});

const {
  ctaText = 'Subir archivo',
  loadingText = 'Cargando archivo',
  removeLabel = 'Quitar archivo',
  label,
  formatsHint,
  maxSizeHint,
  accept,
  maxSizeBytes,
  loading,
  id,
  name,
  required,
  disabled,
  form,
  as = 'div',
} = defineProps<TFileUploadProps>();

const emit = defineEmits<TFileUploadEmits>();
defineSlots<TFileUploadSlots>();

const model = defineModel<File | null>({ default: null });

const inputRef = ref<HTMLInputElement | null>(null);
const generatedId = useId();
const inputId = computed(() => id ?? generatedId);

const { wrapperAttrs, staticAttrs, listenerAttrs } = useForwardedAttrs();

const state = computed<'empty' | 'loading' | 'loaded'>(() => {
  if (loading) {
    return 'loading';
  }
  if (model.value) {
    return 'loaded';
  }
  return 'empty';
});

const acceptAttribute = computed(() => {
  if (!accept || accept.length === 0) {
    return undefined;
  }
  return accept.map((ext) => `.${ext}`).join(',');
});

const showHints = computed(() => state.value === 'empty' && (Boolean(formatsHint) || Boolean(maxSizeHint)));

const syncInputToModel = () => {
  const input = inputRef.value;
  if (!input) {
    return;
  }
  const file = model.value;
  const currentFile = input.files?.[0] ?? null;
  if (file === currentFile) {
    return;
  }
  if (!file) {
    input.value = '';
    return;
  }
  try {
    const dt = new DataTransfer();
    dt.items.add(file);
    input.files = dt.files;
  } catch {}
};

const handleFileChange = () => {
  const input = inputRef.value;
  if (!input) {
    return;
  }
  const file = input.files?.[0];
  if (!file) {
    return;
  }

  if (accept?.length) {
    const filename = file.name.toLowerCase();
    const allowed = accept.some((ext) => filename.endsWith(`.${ext.toLowerCase()}`));
    if (!allowed) {
      syncInputToModel();
      emit('error', 'bad-format');
      return;
    }
  }

  if (maxSizeBytes !== undefined && file.size > maxSizeBytes) {
    syncInputToModel();
    emit('error', 'too-large');
    return;
  }

  model.value = file;
};

const handleRemove = () => {
  if (disabled) {
    return;
  }
  model.value = null;
};

onMounted(syncInputToModel);
watch(model, syncInputToModel);

defineExpose({ inputRef });
</script>

<template>
  <Primitive
    v-bind="wrapperAttrs"
    :as="as"
    class="t-file-upload"
    :data-state="state"
    :data-disabled="disabled || undefined"
  >
    <div v-if="label || $slots.label" class="t-file-upload__label">
      <slot name="label">
        <TTypography variant="b3" font-weight="semibold">
          {{ label }}
        </TTypography>
      </slot>
    </div>

    <label
      v-if="state === 'empty'"
      v-bind="listenerAttrs"
      class="t-file-upload__box"
      data-state="empty"
      :for="inputId"
    >
      <TIcon icon="paper-clip" size="small" color="gray.main" />
      <TTypography variant="b3" class="t-file-upload__cta-text">
        <slot name="cta">{{ ctaText }}</slot>
      </TTypography>
    </label>

    <div
      v-else-if="state === 'loading'"
      v-bind="listenerAttrs"
      class="t-file-upload__box"
      data-state="loading"
    >
      <TIcon icon="arrow-path" size="small" color="primary.main" />
      <TTypography variant="b3" class="t-file-upload__cta-text" color="primary.main">
        <slot name="loading">
          {{ loadingText }}
        </slot>
      </TTypography>
    </div>

    <div
      v-else
      v-bind="listenerAttrs"
      class="t-file-upload__box"
      data-state="loaded"
    >
      <TIcon icon="document" size="small" color="primary.main" />
      <div class="t-file-upload__filename">
        <slot name="file" :file="model as File">
          <TTypography variant="b3" color="primary.main" class="t-file-upload__filename-text">
            {{ (model as File).name }}
          </TTypography>
        </slot>
      </div>
      <button
        type="button"
        class="t-file-upload__remove"
        :aria-label="removeLabel"
        :disabled="disabled"
        @click="handleRemove"
      >
        <TIcon icon="x-circle" color="#FFFFFF" :width="18" :height="18" />
      </button>
    </div>

    <input
      :id="inputId"
      ref="inputRef"
      v-bind="staticAttrs"
      class="t-file-upload__input"
      type="file"
      title=""
      :name="name"
      :required="required"
      :disabled="disabled || loading"
      :form="form"
      :accept="acceptAttribute"
      @change="handleFileChange"
    >

    <div v-if="showHints" class="t-file-upload__hints">
      <TTypography v-if="formatsHint" variant="b4" color="gray.medium1">
        {{ formatsHint }}
      </TTypography>
      <span v-else />
      <TTypography v-if="maxSizeHint" variant="b4" color="gray.medium1">
        {{ maxSizeHint }}
      </TTypography>
    </div>
  </Primitive>
</template>

<style scoped lang="scss">
.t-file-upload {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  width: 100%;

  &[data-disabled] &__box[data-state='empty'] {
    cursor: not-allowed;
    opacity: 0.5;

    &:hover {
      background-color: transparent;
    }
  }

  &[data-disabled] &__remove {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:focus-within &__box[data-state='empty'] {
    border: 1px dashed var(--color-brand-9);
    background-color: var(--color-brand-a-3);
    outline: none;
  }

  &__label {
    display: flex;
  }

  &__box {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    padding: var(--space-5) var(--space-4);
    border-radius: var(--radius-md);
    position: relative;
    transition: background-color 150ms ease, border-color 150ms ease;

    &[data-state='empty'] {
      border: 1px dashed var(--color-gray-4);
      background-color: transparent;
      cursor: pointer;

      &:hover {
        background-color: var(--color-gray-1);
      }
    }

    &[data-state='loading'] {
      border: 1px dashed var(--color-brand-9);
      background-color: var(--color-brand-a-3);
    }

    &[data-state='loaded'] {
      border: 1px solid var(--color-brand-9);
      background-color: var(--color-brand-a-3);
      padding: var(--space-2) var(--space-4);
    }
  }

  &__input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    border: 0;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  &__cta-text {
    text-decoration: underline;
  }

  &__filename {
    min-width: 0;
    overflow: hidden;
  }

  &__filename-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-decoration: underline;
  }

  &__remove {
    position: absolute;
    top: calc(-1 * var(--space-2));
    right: -2px;
    width: 18px;
    height: 18px;
    background: radial-gradient(
      circle,
      var(--color-brand-9) 0,
      var(--color-brand-9) 6px,
      transparent 7.5px
    );
    border: none;
    border-radius: var(--radius-full);
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
  }

  &__hints {
    display: flex;
    justify-content: space-between;
    gap: var(--space-4);
  }
}
</style>
